import React, { useState, useEffect, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';

interface WrapperProps {
  isOpen?: boolean;
  backdrop?: string;
}

const Content = styled.div`
  background-color: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: transform 0.3s ease-out;
  z-index: 9999;
`;

const Overlay = styled.div`
  background-color: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  bottom: 0;
  display: flex;
  left: 0;
  opacity: 0;
  position: fixed;
  right: 0;
  top: 0;
  transition: all 0.3s;
`;

const Wrapper = styled.div<WrapperProps>`
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  transition: all 0.3s;
  visibility: hidden;
  z-index: 99999;

  ${Content} {
    transform: translate(0, -30px);
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      display: flex;
      opacity: 1;
      pointer-events: auto;
      visibility: visible;

      ${Content} {
        transform: translate(0, 0);
      }

      ${Overlay} {
        opacity: 1;
      }
    `}
`;

const HeaderWrapper = styled.div`
  align-items: center;
  border-bottom: 1px solid #EEF2F6;
  display: flex;
  justify-content: center;
  padding: 16px 20px;
  background-color: #FFFFFF;
`;

const HeaderTitle = styled.span`
  font-family: 'Outfit', 'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #0F1A2B;
  letter-spacing: -0.01em;
  text-align: center;
`;

const Body = styled.div`
  padding: 16px;
  position: relative;
  background-color: #FFFFFF;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 20px 20px 20px;
  background-color: #FFFFFF;
`;

interface HeaderProps {
  children?: ReactNode;
  [key: string]: any;
}

const Header: React.FC<HeaderProps> = ({ children, ...props }) => {
  return (
    <HeaderWrapper {...props}>
      <HeaderTitle>{children}</HeaderTitle>
    </HeaderWrapper>
  );
};

interface ModalProps {
  children: ReactNode;
  isOpen: boolean;
}

export interface ModalComponent {
  (props: ModalProps): any;
  Header: typeof Header;
  Body: typeof Body;
  Footer: typeof Footer;
}

const Modal = (({ children, isOpen }: ModalProps) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const modalElement = (
    <Wrapper isOpen={isOpen} backdrop={'static'}>
      <Overlay />
      <Content>{children}</Content>
    </Wrapper>
  );

  if (!mounted || typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(modalElement, document.body);
}) as ModalComponent;

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;

export default Modal;
